export const throttle = <TFunc extends (...args: unknown[]) => void>(
    func: TFunc,
    delay: number,
) => {
    let lastCall = 0;
    let timeout: ReturnType<typeof setTimeout> = null;

    const wrapped = (...args: unknown[]) => {
        const now = Date.now();

        const call = () => {
            lastCall = now;
            clearTimeout(timeout);
            timeout = null;
            func(...args);
        };

        if (now - lastCall < delay) {
            if (!timeout) {
                timeout = setTimeout(call, delay);
            }
        } else {
            call();
        }
    };

    return wrapped as TFunc;
};

export const debounce = <TFunc extends (...args: unknown[]) => void>(
    func: TFunc,
    delay: number,
) => {
    let timeout: ReturnType<typeof setTimeout> = null;

    const wrapped = (...args: unknown[]) => {
        if (timeout) clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), delay);
    };

    Object.assign(wrapped, {
        cancel() {
            if (timeout) clearTimeout(timeout);
        },
    });

    return wrapped as TFunc & { cancel: () => void };
};

export const multiClick = <T extends (...args: unknown[]) => void>(
    callBack: T,
    count = 5,
    delay = 500,
) => {
    let counter = 0,
        timer;

    return (...args) => {
        counter += 1;
        clearTimeout(timer);
        timer = setTimeout(() => {
            counter = 0;
        }, delay);

        if (counter >= count) {
            callBack(...args);
            counter = 0;
        }
    };
};

export const createPassphraseListener = <T extends (...args: unknown[]) => void>(
    passphrase: string,
    callBack: T,
) => {
    let index = 0;

    const handler = (event: KeyboardEvent) => {
        const key = event.key;
        if (passphrase[index] === key) {
            index += 1;
        } else {
            index = 0;
        }

        if (index === passphrase.length) {
            index = 0;
            callBack();
        }
    };

    document.addEventListener('keydown', handler);

    return () => {
        document.removeEventListener('keydown', handler);
    };
};
